import React from "react";
import { Plus } from "lucide-react";
import ConditionCard from "./ConditionCard";
import ConditionNode from "./ConditionNode";
import {
  useDeleteSequenceTransition,
  useGetSequenceTransitions,
  useSaveSequenceFallback,
} from "@/features/campaigns/hooks/useCampaign";

const END_CAMPAIGN = "__end_campaign__";

const NoBranchSelector = ({ condition, steps = [], onAddStep }) => {
  const { data: transitions = [], isLoading } = useGetSequenceTransitions(condition?.id);
  const { mutate: saveFallback, isPending: isSaving } = useSaveSequenceFallback();
  const { mutate: deleteTransition, isPending: isDeleting } = useDeleteSequenceTransition();
  const fallback = transitions.find((transition) => transition.fallback);
  const selectedValue = fallback
    ? fallback.to_step_id || END_CAMPAIGN
    : "";
  const availableSteps = steps
    .filter(
      (step) =>
        step.id !== condition.id &&
        Number(step.step_order) > Number(condition.step_order) &&
        Boolean(step.channel) &&
        step.conditions == null,
    )
    .sort((a, b) => Number(a.step_order) - Number(b.step_order));

  const handleChange = (event) => {
    const value = event.target.value;
    if (!value) {
      if (fallback) {
        deleteTransition({ transitionId: fallback.id, fromStepId: condition.id });
      }
      return;
    }
    saveFallback({
      fromStepId: condition.id,
      toStepId: value === END_CAMPAIGN ? null : value,
    });
  };

  return (
    <div className="flex w-full flex-col items-center gap-2">
      <select
        aria-label={`No branch for ${condition.conditions || "condition"}`}
        value={selectedValue}
        onChange={handleChange}
        disabled={isLoading || isSaving || isDeleting}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-secondary dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200"
      >
        <option value="">Continue normal sequence</option>
        <option value={END_CAMPAIGN}>End campaign</option>
        {availableSteps.map((step) => (
          <option key={step.id} value={step.id}>
            Step {step.step_order}: {step.action || step.channel}
          </option>
        ))}
      </select>
      {!availableSteps.length && (
        <span className="text-center text-[0.65rem] text-gray-400">
          No later action step is available yet.
        </span>
      )}
      <button
        type="button"
        onClick={() => onAddStep?.(condition)}
        disabled={isLoading || isSaving || isDeleting}
        className="inline-flex items-center gap-1 rounded-md border border-dashed border-secondary px-2.5 py-1.5 text-[0.65rem] font-medium text-secondary transition-colors hover:bg-secondary/5 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Plus size={13} strokeWidth={2} />
        Add No branch step
      </button>
    </div>
  );
};

const ConditionWithBranches = ({
  condition,
  stepOrder,
  onEdit,
  onDelete,
  steps,
  onAddBranchStep,
}) => (
  <div className="w-full flex flex-col items-center relative">
    <div className="w-full flex flex-col items-center justify-center">
      <ConditionCard
        condition={condition}
        stepOrder={stepOrder}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>

    <div className="w-px h-6 border-l border-dashed border-gray-400 dark:border-gray-500" />

    {/* Horizontal split */}
    <div className="w-[500px] h-8 border-t border-l border-r border-dashed border-gray-400 dark:border-gray-500 rounded-t-xl" />

    {/* Dynamic Branches using Flexbox and CSS Transform for perfect edge alignment */}
    <div className="flex w-[500px] items-stretch">
      {/* Left Branch */}
      <div
        className="w-1/2 flex flex-col items-center relative"
        style={{ transform: "translateX(-50%)" }}
      >
        {/* Continuous background line */}
        <div className="absolute top-0 bottom-0 w-px border-l border-dashed border-gray-400 dark:border-gray-500 z-0" />

        <div className="w-[435px] flex flex-col items-center relative z-10 pb-6">
          <div className="flex items-center gap-2 bg-success-50 dark:bg-success-950/40 p-1.5 rounded-full">
            <span className="text-xs text-success-500 font-medium">Yes</span>
          </div>
          <div className="h-6 w-full" />
          <ConditionNode data={condition} onEdit={onEdit} onDelete={onDelete} />
        </div>
      </div>

      {/* Right Branch */}
      <div
        className="w-1/2 flex flex-col items-center relative"
        style={{ transform: "translateX(50%)" }}
      >
        {/* Continuous background line */}
        <div className="absolute top-0 bottom-0 w-px border-l border-dashed border-gray-400 dark:border-gray-500 z-0" />

        <div className="w-[435px] flex flex-col items-center relative z-10 pb-6">
          <div className="flex items-center gap-2 bg-error-50 dark:bg-error-950/40 p-1.5 rounded-full">
            <span className="text-xs text-error-500 font-medium">No</span>
          </div>
          <div className="h-6 w-full" />
          <NoBranchSelector
            condition={condition}
            steps={steps}
            onAddStep={onAddBranchStep}
          />
        </div>
      </div>
    </div>

    {/* Bottom join matching the top split */}
    <div className="w-[500px] h-8 border-b border-l border-r border-dashed border-gray-400 dark:border-gray-500 rounded-b-xl" />

    {/* Tail continuing down to the next node */}
    <div className="w-px h-6 border-l border-dashed border-gray-400 dark:border-gray-500" />
  </div>
);

export default ConditionWithBranches;
